/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  DateRangePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { verificationClient } from '../../../../services/verificationClient';
import { ActionAttempt, PolicyAction } from '../../../../types/VerificationTypes';


type DateRange = { from: Date | undefined; to?: Date };

const ALL = 'all';


type TagVariant = 'failed' | 'pending' | 'processing';
// Visual distinction between the three "not allow" outcomes:
//   deny      → hard fail, action denied outright (red)
//   remediate → user has an in-band path to fix this (blue)
//   step_up   → user needs to re-verify a fresh factor (amber)
const DECISION_COLOR: Record<string, TagVariant> = {
  deny: 'failed',
  remediate: 'processing',
  step_up: 'pending',
};


function useQueryParam(name: string): string {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name) || '';
}


/**
 * "Failed" = decision != 'allow' (deny + step_up).
 *
 * The backend filter is `failed_only=true`, which makes pagination correct
 * (server-side filter + cursor). The dropdown lets the operator narrow
 * further to just deny or just step_up.
 */
const FailedActionAttemptsTab: React.FC = () => {
  const initialEmpId = useQueryParam('employee_id');
  const [employeeIdFilter, setEmployeeIdFilter] = useState(initialEmpId);
  const [actionCodeFilter, setActionCodeFilter] = useState('');
  const [decisionFilter, setDecisionFilter] = useState<'' | 'deny' | 'remediate' | 'step_up'>('deny');
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const [rows, setRows] = useState<ActionAttempt[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showContext, setShowContext] = useState<string | null>(null);
  const [actions, setActions] = useState<PolicyAction[]>([]);

  const [actionsError, setActionsError] = useState(false);

  useEffect(() => {
    setActionsError(false);
    verificationClient
      .listActions()
      .then(setActions)
      .catch((err) => {
        setActionsError(true);
        apiResponseHandler.handleApiError(err);
      });
  }, []);

  // Cursor is passed in explicitly per call. Keeping cursor in component state
  // and reading it inside the callback would close over a stale value on the
  // synchronous "Load more" path (setCursor + load fire in the same tick), so
  // the next page would refetch the previous one. Same reason we don't put it
  // in the callback's deps.
  const load = useCallback(
    async (pageCursor: string | null = null) => {
      setLoading(true);
      try {
        const params: Parameters<typeof verificationClient.listActionAttempts>[0] = {
          failed_only: true,
        };
        if (employeeIdFilter) {
          const parsed = parseInt(employeeIdFilter, 10);
          if (!Number.isNaN(parsed)) params.employee_id = parsed;
        }
        if (actionCodeFilter) params.action_code = actionCodeFilter;
        if (decisionFilter) params.decision = decisionFilter;
        if (dateRange?.from) params.since = dateRange.from.toISOString();
        if (dateRange?.to) params.until = dateRange.to.toISOString();
        if (pageCursor) params.cursor = pageCursor;
        const page = await verificationClient.listActionAttempts(params);
        setRows((prev) => (pageCursor ? [...prev, ...page.results] : page.results));
        setNextCursor(page.next_cursor);
      } catch (err) {
        apiResponseHandler.handleApiError(err);
      } finally {
        setLoading(false);
      }
    },
    [employeeIdFilter, actionCodeFilter, decisionFilter, dateRange]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  const handleReset = () => {
    setDateRange(null);
    setActionCodeFilter('');
    setDecisionFilter('deny');
    setEmployeeIdFilter('');
  };

  const actionOptions = actions
    .map((a) => ({ value: a.code, label: a.name || a.code }))
    .sort((x, y) => x.label.localeCompare(y.label));

  const displayRows = rows;

  return (
    <div>
      <div className="mb-md flex flex-wrap items-end gap-xs">
        <div>
          <DateRangePicker
            label="Date range"
            dateRange={dateRange ?? undefined}
            onDateRangeChange={(range) => setDateRange((range as DateRange | undefined) ?? null)}
            placeholder="Pick a date range"
          />
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Employee ID</span>
          <Input
            type="number"
            className="w-[160px]"
            value={employeeIdFilter}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmployeeIdFilter(e.target.value)
            }
            placeholder="e.g. 102837"
          />
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Action</span>
          <Select
            value={actionCodeFilter || ALL}
            onValueChange={(val) => setActionCodeFilter(val === ALL ? '' : val)}
            disabled={actionsError}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue
                placeholder={actionsError ? 'Action registry unavailable' : 'All actions'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All actions</SelectItem>
              {actionOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-xxs">
          <span className="text-sm font-medium text-greyscale-text-title">Decision</span>
          <Select
            value={decisionFilter || ALL}
            onValueChange={(val) =>
              setDecisionFilter(val === ALL ? '' : (val as 'deny' | 'remediate' | 'step_up'))
            }
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All decisions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All decisions</SelectItem>
              <SelectItem value="deny">deny</SelectItem>
              <SelectItem value="remediate">remediate</SelectItem>
              <SelectItem value="step_up">step_up</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button variant="outline" onClick={handleReset}>
          Reset
        </Button>
      </div>

      <div className="rounded-2xl border border-greyscale-border-default bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Blocked by</TableHead>
              <TableHead>Remediable</TableHead>
              <TableHead>Step-up</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((a) => (
              <React.Fragment key={a.id}>
                <TableRow>
                  <TableCell className="text-xs">
                    {new Date(a.date_created).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">{a.employee_id}</TableCell>
                  <TableCell className="text-xs">
                    {a.employee_name || (
                      <span className="text-greyscale-text-caption">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{a.action_code}</TableCell>
                  <TableCell>
                    <Tag variant={DECISION_COLOR[a.decision] || 'failed'}>{a.decision}</Tag>
                  </TableCell>
                  <TableCell className="text-xs">{a.channel_flavour}</TableCell>
                  <TableCell className="text-xs text-error-text-label">
                    {a.blocked_by && a.blocked_by.length > 0 ? (
                      <div className="flex flex-col gap-xxxs">
                        {a.blocked_by.map((c) => (
                          <span key={c}>{c}</span>
                        ))}
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(a.remediable_codes || []).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(a.step_up_required || []).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="link"
                      size="xs"
                      onClick={() => setShowContext(showContext === a.id ? null : a.id)}
                    >
                      {showContext === a.id ? 'hide' : 'context'}
                    </Button>
                  </TableCell>
                </TableRow>
                {showContext === a.id && (
                  <TableRow>
                    <TableCell colSpan={10} className="bg-greyscale-surface-subtle">
                      <pre className="m-none text-xxs">
                        {JSON.stringify(
                          { context: a.context, fraud_signals: a.fraud_signals },
                          null,
                          2
                        )}
                      </pre>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {displayRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10} className="py-md text-center text-greyscale-text-caption">
                  No failed attempts match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && (
          <div className="flex justify-center py-sm">
            <Loader2 className="animate-spin text-secondary-text-label" size={20} />
          </div>
        )}
        {nextCursor && (
          <div className="flex justify-center py-sm">
            <Button variant="link" onClick={() => load(nextCursor)} disabled={loading}>
              Load more →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FailedActionAttemptsTab;
